import { useEffect } from 'react';
import {
  THEME_STORAGE_KEY,
  applyThemeToDOM,
  bootstrapTheme,
  loadThemeFromStorage,
  resolveTheme,
} from '@/lib/themePreference';

/**
 * Redux-free theme hook that runs on every route (public and protected).
 *
 * Responsibilities:
 * - Re-apply the saved theme on mount (idempotent; the inline script in
 *   `index.html` already does this before React hydrates).
 * - Listen for OS colour-scheme changes when preference is `system`.
 * - Sync across tabs via the `storage` event so flipping theme in one tab
 *   updates the others without a reload.
 *
 * The full `useTheme` hook (in `./useTheme.ts`) handles server reconciliation
 * and the `setTheme` mutation, but it requires Redux and therefore only runs
 * inside the authenticated shell. This hook fills the public-route gap.
 */
export function useThemeBootstrap(): void {
  useEffect(() => {
    // Reapply on mount in case something outside React changed the DOM state.
    bootstrapTheme();

    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    const onMediaChange = () => {
      // Only react while the user has asked to follow the OS.
      if (loadThemeFromStorage() === 'system') {
        applyThemeToDOM(resolveTheme('system'));
      }
    };
    mql.addEventListener('change', onMediaChange);

    const onStorage = (event: StorageEvent) => {
      if (event.key !== THEME_STORAGE_KEY) return;
      applyThemeToDOM(resolveTheme(loadThemeFromStorage()));
    };
    window.addEventListener('storage', onStorage);

    return () => {
      mql.removeEventListener('change', onMediaChange);
      window.removeEventListener('storage', onStorage);
    };
  }, []);
}
